import { FC, ReactNode } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { ChevronRight, Plus, ExternalLink } from "lucide-react";
import { cn } from "@/shared/utils";

interface ChildCardProps {
  /** Title of the card */
  title: string;
  /** Short description/subtitle */
  subtitle?: string;
  /** Detailed description of the feature */
  description: string;
  /** Route to navigate to */
  to: string;
  /** Icon or logo to display */
  icon?: ReactNode;
  /** Background gradient colors */
  gradient?: {
    from: string;
    to: string;
  };
  /** Icon letter/character to display in the gradient circle */
  iconLetter?: string;
  /** Call to action text */
  ctaText?: string;
  /** Whether this card is disabled/coming soon */
  disabled?: boolean;
  /** Card variant */
  variant?: "default" | "featured" | "minimal";
  /** Status badge */
  status?: "active" | "beta" | "coming-soon" | "maintenance";
  /** External link indicator */
  external?: boolean;
  /** Custom className */
  className?: string;
}

/**
 * **ChildCard**
 *
 * Enhanced reusable card component for displaying child applications/features
 * in intermediate navigation pages (like HealthApp, PersonalDevelopmentApp, etc.)
 *
 * Features:
 * - Multiple variants (default, featured, minimal)
 * - Status badges (active, beta, coming-soon, maintenance)
 * - External link indicators
 * - Improved accessibility
 * - Design system compliance
 */
export const ChildCard: FC<ChildCardProps> = ({
  title,
  subtitle,
  description,
  to,
  icon,
  gradient = { from: "from-blue-500", to: "to-blue-600" },
  iconLetter,
  ctaText = "View Dashboard",
  disabled = false,
  variant = "default",
  status,
  external = false,
  className,
}) => {
  // Status badge configuration
  const getStatusBadge = () => {
    if (!status) return null;

    const statusConfig = {
      active: { variant: "success" as const, label: "Active" },
      beta: { variant: "warning" as const, label: "Beta" },
      "coming-soon": { variant: "info" as const, label: "Coming Soon" },
      maintenance: { variant: "destructive" as const, label: "Maintenance" },
    };

    const config = statusConfig[status];
    return (
      <Badge variant={config.variant} size="sm">
        {config.label}
      </Badge>
    );
  };

  // Variant-specific styling
  const getVariantStyles = () => {
    switch (variant) {
      case "featured":
        return "ring-2 ring-blue-500/20 bg-gradient-to-br from-blue-500/5 to-purple-500/5";
      case "minimal":
        return "bg-transparent border-slate-700/50";
      default:
        return "bg-slate-800/50 border-slate-700";
    }
  };

  const CardContent = () => (
    <Card
      variant="default"
      padding="lg"
      className={cn(
        "group h-full flex flex-col transition-all duration-200",
        "hover:border-blue-500/50 hover:shadow-lg hover:shadow-blue-500/20",
        "focus-within:ring-2 focus-within:ring-blue-500/50 focus-within:ring-offset-2",
        getVariantStyles(),
        className,
      )}
    >
      <div className="flex items-start space-x-4 flex-1">
        {/* Icon */}
        <div
          className={cn(
            "w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0",
            "group-hover:scale-105 transition-transform duration-200",
            // Only apply gradient background if no icon is provided (fallback to iconLetter)
            !icon && "bg-gradient-to-br",
            !icon && gradient.from,
            !icon && gradient.to,
          )}
        >
          {icon || (
            <span className="text-white font-bold text-xl">
              {iconLetter || title.charAt(0).toUpperCase()}
            </span>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="mb-3">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-xl font-semibold text-white group-hover:text-blue-300 transition-colors">
                {title}
              </h3>
              {getStatusBadge()}
            </div>
            {subtitle && <p className="text-sm text-slate-400 font-medium">{subtitle}</p>}
          </div>

          <p className="text-slate-300 text-sm leading-relaxed mb-4 flex-1">{description}</p>

          <div className="flex items-center justify-between mt-auto">
            <div className="flex items-center text-blue-400 text-sm font-medium group-hover:text-blue-300 transition-colors">
              <span>{ctaText}</span>
              {external ? (
                <ExternalLink className="w-4 h-4 ml-2" />
              ) : (
                <ChevronRight className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform" />
              )}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );

  if (disabled) {
    return (
      <Card
        variant="default"
        padding="lg"
        className={cn(
          "opacity-60 h-full flex flex-col cursor-not-allowed",
          "bg-slate-800/30 border-slate-700/50",
          className,
        )}
      >
        <div className="flex items-start space-x-4 flex-1">
          {/* Icon */}
          <div className="w-14 h-14 bg-gradient-to-br from-slate-600 to-slate-700 rounded-xl flex items-center justify-center flex-shrink-0">
            <Plus className="w-6 h-6 text-slate-400" />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0 flex flex-col">
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-xl font-semibold text-slate-400">{title}</h3>
                <Badge variant="info" size="sm">
                  Coming Soon
                </Badge>
              </div>
              {subtitle && <p className="text-sm text-slate-500 font-medium">{subtitle}</p>}
            </div>

            <p className="text-slate-500 text-sm leading-relaxed flex-1">{description}</p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Link
      to={to}
      className="block focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:ring-offset-2 rounded-lg"
      aria-label={`Navigate to ${title}`}
    >
      <CardContent />
    </Link>
  );
};

/**
 * **ComingSoonCard**
 *
 * Specialized card for features that are coming soon
 * Enhanced with better styling and status badges
 */
export const ComingSoonCard: FC<
  Omit<ChildCardProps, "to" | "ctaText" | "disabled"> & { message?: string }
> = ({ title, subtitle, description, message = "Coming Soon", ...props }) => (
  <ChildCard
    {...props}
    title={title}
    {...(subtitle ? { subtitle } : {})}
    description={description}
    to="#"
    ctaText={message}
    disabled={true}
    status="coming-soon"
  />
);

/**
 * **FeaturedCard**
 *
 * Specialized card for featured/promoted applications
 */
export const FeaturedCard: FC<ChildCardProps> = (props) => (
  <ChildCard {...props} variant="featured" status="active" />
);

/**
 * **BetaCard**
 *
 * Specialized card for beta applications
 */
export const BetaCard: FC<ChildCardProps> = (props) => <ChildCard {...props} status="beta" />;
